import React from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Card, HorizontalLine } from '@components/ringil3/styles';
import { ExpeditionStatusEnum, LocationPreviewV4OutDTO } from '@api/ringil-api.v2';
import EditableHeading3 from '@components/ringil3/Heading/EditableHeading3';
import { formatLocationNotino, isNotBlank, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { printDeliverySlots } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot';
import LoadingReferenceBadge from '@components/ringil3/Badges/LoadingReferenceBadge';
import LinkedResBadge from '@components/ringil3/Badges/LinkedResBadge';
import RingilOrder from '@components/ringil3/RingilOrder/RingilOrder';
import SvgMappoint from '@components/ringil3/icons/Mappoint';
import SvgCalendar from '@components/ringil3/icons/Calendar';
import SvgContacts from '@components/ringil3/icons/Contacts';
import SvgLoading from '@components/ringil3/icons/Loading';
import SvgUnloading from '@components/ringil3/icons/Unloading';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import {
  useCreateEditExpeditionLocation,
} from '@components/ringil3/Features/Expedition/Sidebars/EditLocationAndContactSidebar.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import {
  useCreateEditManualExpeditionLocation,
} from '@components/ringil3/Features/Expedition/Sidebars/EditManualLocationAndContactSidebar.tsx';
import {
  useCreateEditExpeditionTimeSlotsSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/EditExpeditionTimeSlotsSidebar.tsx';
import {
  useCreateEditLoadingOrderSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/EditLoadingOrdersSidebar.tsx';
import { Order } from '@components/framework/orders/common.tsx';
import COText from '@app/pages/COText/COText.tsx';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';

interface ComponentProps {
  location: LocationPreviewV4OutDTO;
  applicationId: string;
  expeditionId: string;
  allUnloadingLocations: { id: string; name: string }[];
  onReload: () => void;
  isLastLocation: boolean;
  isFirstLocation?: boolean;
  canEdit: boolean;
  expeditionStatus: ExpeditionStatusEnum;
  /** Expedition created from / linked to a supplier notification. */
  linkedToSN?: boolean;
  testIdPrefix?: string;
  volumeOfOrderEnabled?: boolean;
  secured?: SecuredConfig;
}

const ExpeditionLocation: React.FC<ComponentProps> = ({ location, applicationId, expeditionId, allUnloadingLocations, onReload, isLastLocation, isFirstLocation = false, canEdit, expeditionStatus, linkedToSN = false, testIdPrefix, volumeOfOrderEnabled = false, secured }) => {
  const { tr } = useTranslationLgs();
  const { decorate } = useSecuredDecoration(secured);
  // RD-2945: a stop picked from the location list can be swapped for another one in any expedition status (like a
  // manual address edit), unless the expedition is linked to an SN or the stop has a reservation linked (BE enforces the same).
  const canChangeLocation = canEdit && !linkedToSN && isNullOrUndefined(location.linkedReservation);
  const { visible: visibleEditLocation, setVisible: setVisibleEditLocation, EditExpeditionLocationSidebar } = useCreateEditExpeditionLocation();
  const { visible: visibleEditManualLocation, setVisible: setVisibleEditManualLocation, EditExpeditionManualLocationSidebar } = useCreateEditManualExpeditionLocation();
  const { visible: visibleEditTimeSlot, setVisible: setVisibleEditSlotLocation, EditExpeditionTimeSlotsSidebar } = useCreateEditExpeditionTimeSlotsSidebar();
  const { visible: visibleEditOrders, setVisible: setVisibleEditOrders, EditLoadingOrderSidebar } = useCreateEditLoadingOrderSidebar();

  const canLoadNew = (location: LocationPreviewV4OutDTO) => {
    if (isLastLocation) return false;
    return isNullOrUndefined(location.loadedOrders) || location.loadedOrders.length === 0;
  };

  const locationNameTestId = testIdPrefix ? `${testIdPrefix}-locationName` : undefined;
  const addressTestId = testIdPrefix ? `${testIdPrefix}-address` : undefined;
  const mainContactNameTestId = testIdPrefix ? `${testIdPrefix}-mainContact` : undefined;
  const mainContactEmailTestId = testIdPrefix ? `${testIdPrefix}-mainContactEmail` : undefined;
  const mainContactPhoneTestId = testIdPrefix ? `${testIdPrefix}-mainContactPhone` : undefined;
  const timeslotPrefix = testIdPrefix ? `${testIdPrefix}-timeslot` : undefined;
  const timezonePrefix = testIdPrefix ? `${testIdPrefix}-timezone` : undefined;

  const loadingPrefix = testIdPrefix ? testIdPrefix.replace(/^location/, 'locationLoading') : undefined;
  const unloadingPrefix = testIdPrefix ? testIdPrefix.replace(/^location/, 'locationUnloading') : undefined;

  return decorate(
    <ExpeditionLocationInner>
      <Card>
        <Grid>
          <Col>
            <div data-testid={locationNameTestId}>
              <EditableHeading3 editable={false} title={location.name} icon={<SvgMappoint />}></EditableHeading3>
            </div>
            <div data-testid={addressTestId}>{formatLocationNotino(location.postalCode, location.city, location.country, location.streetNr)}</div>
            <COTimezone timezoneId={location.timezoneId} dataTestId={timezonePrefix}/>
            {isNotBlank(location?.loadingReference) && <LoadingReferenceBadge loadingReference={location.loadingReference} />}
            {isNotNullOrUndefined(location.linkedReservation) && <LinkedResBadge linkedReservation={location.linkedReservation} />}
          </Col>
          <Col>
            <EditableHeading3
              editable={canEdit}
              title={tr('ExpeditionLocation.kontakty', 'Kontakty')}
              icon={<SvgContacts />}
              onEditClick={() => {
                if (location.companyLocationId) {
                  setVisibleEditLocation(true);
                } else {
                  setVisibleEditManualLocation(true);
                }
              }}
            />
            {isNotNullOrUndefined(location.selectedPerson) && (
              <div>
                <div data-testid={mainContactNameTestId}>{location.selectedPerson.contactName}</div>
                <div data-testid={mainContactPhoneTestId}>{location.selectedPerson.contactPhone}</div>
                <div data-testid={mainContactEmailTestId}>{location.selectedPerson.contactEmail}</div>
              </div>
            )}
            {(location.additionalContacts.length > 0 || location.notifySelectedPerson)  &&  (
              <AdditionalContacts>
                <COHeadline variant={'h5'} title={tr('ExpeditionLocation.notifikovatDalsiOsoby', 'Notifikovat dalsi osoby')}></COHeadline>
                {location.notifySelectedPerson && isNotNullOrUndefined(location.selectedPerson) && (
                  <div className='mt-2'>
                    <div>{location.selectedPerson.contactName}</div>
                    <div>{location.selectedPerson.contactPhone}</div>
                    <div>{location.selectedPerson.contactEmail}</div>
                  </div>
                )}

                {location.additionalContacts.map((p, pIndex) => {
                  const notifyTestId = testIdPrefix ? `${testIdPrefix}-notifyOfTransport${pIndex + 1}` : undefined;
                  return (
                    <Contact key={pIndex} data-testid={notifyTestId}>
                      <div>{p.contactName}</div>
                      <div>{p.contactPhone}</div>
                      <div>{p.contactEmail}</div>
                    </Contact>
                  );
                })}

              </AdditionalContacts>
            )}
          </Col>
        </Grid>
        <div className={'py-4'}>
          <HorizontalLine variant={'tertiary'} />
        </div>
        <div>
          <Col>
            <EditableHeading3
              editable={canEdit}
              title={tr(`ExpeditionLocation.ocekavanaManipulace`, `Ocekavana manipulace`)}
              icon={<SvgCalendar />}
              onEditClick={() => setVisibleEditSlotLocation(true)}
            />
            <div>{printDeliverySlots(location.deliverySlot, tr, timeslotPrefix)}</div>
          </Col>
        </div>
      </Card>
      {(location?.loadedOrders?.length > 0 || location?.unloadedOrders?.length > 0) && (
        <Card>
          <Col>
            {location.loadedOrders.length > 0 && (
              <div data-testid={loadingPrefix}>
                <EditableHeading3
                  editable={canEdit}
                  title={tr('ExpeditionLocation.nakladka', 'Nakladka')}
                  icon={<SvgLoading />}
                  onEditClick={() => setVisibleEditOrders(true)}
                />
                <COBadge variant="filled" type={'loading'}>
                  <COText label={tr('ExpeditionLocation.nakladka', 'Nakládka')} />
                </COBadge>
                <div className={'flex flex-col gap-4'}>
                  {location.loadedOrders.map((order, orderIndex) => {
                    return <RingilOrder key={orderIndex} type={'loading'} order={order} dataTestIdPrefix={loadingPrefix} orderIndex={orderIndex} showVolume={volumeOfOrderEnabled} />;
                  })}
                </div>
              </div>
            )}
            {location.loadedOrders.length > 0 && location.unloadedOrders.length > 0 && <HorizontalLine variant={'quaternary'} />}

            {location.unloadedOrders.length > 0 && (
              <div data-testid={unloadingPrefix}>
                <EditableHeading3 editable={false} title={tr('ExpeditionLocation.vykladka', 'Vykladka')}
                                  icon={<SvgUnloading />} />
                <COBadge variant="filled" type={'unloading'}>
                  <COText label={tr('ExpeditionLocation.vykladka', 'Vykládka')} />
                </COBadge>
                <div className={'flex flex-col gap-4'}>
                  {location.unloadedOrders.map((order, orderIndex) => {
                    return <RingilOrder key={orderIndex} type={'unloading'} order={order} dataTestIdPrefix={unloadingPrefix} orderIndex={orderIndex} showVolume={volumeOfOrderEnabled} />;
                  })}
                </div>
              </div>
            )}
          </Col>
        </Card>
      )}

      {canLoadNew(location) && canEdit && (
        <RowButton
          title={tr('ExpeditionLocation.nakladka', 'Nakladka')}
          icon={<SvgLoading />}
          // Grid items stretch by default; keep the row at its own height instead of matching the
          // location card in the neighbouring column.
          className={'self-start'}
          onClick={() => {
            setVisibleEditOrders(true);
          }}
        />
      )}

      {/**/}
      {visibleEditLocation === true && (
        <EditExpeditionLocationSidebar
          applicationId={applicationId}
          expeditionId={expeditionId}
          companyLocationId={location.companyLocationId}
          locationId={location.id}
          location={location}
          canChangeLocation={canChangeLocation}
          onContinue={() => {
            onReload();
            setVisibleEditLocation(false);
          }}
        />
      )}
      {visibleEditManualLocation === true && (
        <EditExpeditionManualLocationSidebar
          applicationId={applicationId}
          expeditionId={expeditionId}
          locationId={location.id}
          location={location}
          expeditionStatus={expeditionStatus}
          canEdit={canEdit}
          onContinue={() => {
            onReload();
            setVisibleEditManualLocation(false);
          }}
        />
      )}
      {visibleEditTimeSlot === true && (
        <EditExpeditionTimeSlotsSidebar
          localityName={location.name}
          applicationId={applicationId}
          expeditionId={expeditionId}
          locationId={location.id}
          companyLocationId={location.companyLocationId}
          timeslots={location.deliverySlot}
          isFirstLocation={isFirstLocation}
          onContinue={() => {
            onReload();
            setVisibleEditSlotLocation(false);
          }}
        />
      )}
      {visibleEditOrders && (
        <EditLoadingOrderSidebar
          onContinue={() => {
            onReload();
          }}
          allUnloadingLocations={allUnloadingLocations}
          applicationId={applicationId}
          expeditionId={expeditionId}
          localityName={location.name}
          locationId={location.id}
          orders={location.loadedOrders.map(
            t =>
              ({
                orderName: t.orderName,
                items: t.items.map(t => ({
                  cargoTemplateCode: t.cargoTemplate?.code,
                  stackable: t.stackable,
                  amount: t.amount,
                  height: t.height,
                  itemNote: t.itemNote,
                  length: t.length,
                  weight: t.weight === 0 ? null : t.weight,
                  width: t.width,
                  decimalPlaces: t.cargoTemplate?.decimalPlaces,
                  details: t.manipulationUnitInfo,
                })),
                id: t.id,
                orderAmount: t.orderAmount,
                orderDescription: t.orderDescription,
                orderValue: t.orderValue,
                orderNote: t.orderNote,
                unloadingLocationId: t.unloadingLocationId,
                eanCodes: t.loadedEans,
                orderTotalWeight: t.orderTotalWeight,
              } as Order),
          )}
        />
      )}
    </ExpeditionLocationInner>,
  );
};

const Contact = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0;
`;

const AdditionalContacts = styled.div`
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const ExpeditionLocationInner = styled.div`
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  display: grid;
  margin: 1rem 0;
`;

const RightColumnContent = styled.div`
  grid-column: 2;
`;

export default ExpeditionLocation;
